import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, Typography, IconButton, Tooltip, Stack,
  TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Button, Chip,
  FormControl, InputLabel, Select, MenuItem, useTheme, useMediaQuery
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../components/PageHeader.js";
import { DialogHelp } from "../../components/DialogHelp.js";
import { EmptyState } from "../../components/EmptyState.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { DataGridRowActionsMenu } from "../../components/DataGridRowActionsMenu.js";
import { useApi } from "../../hooks/useApi.js";
import { formLabel } from "../../lib/labels.js";
import type { WhatsAppTemplate } from "@kappa-v2/contracts";

// Allineato a produzione: name / message / attachedType (chiave i18n form.whatsapp.*).
const ATTACHED_TYPES = [
  "form.whatsapp.none",
  "form.whatsapp.lastReceipt",
  "form.whatsapp.lastUnpaidReceipts",
  "form.whatsapp.receiptPaid",
  "form.whatsapp.receiptUnpaid",
  "form.whatsapp.genericAttachment"
];

interface FormValues { name: string; message: string; attachedType: string }
const EMPTY: FormValues = { name: "", message: "", attachedType: "form.whatsapp.none" };

export function WhatsAppTemplatesPage() {
  const { t } = useTranslation();
  const { call } = useApi();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const schema = Yup.object({
    name: Yup.string().required(t("whatsapp.templates.nameRequired")),
    message: Yup.string().required(t("whatsapp.templates.messageRequired")),
    attachedType: Yup.string().required()
  });
  const [rows, setRows] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<WhatsAppTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppTemplate | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    call<{ data: WhatsAppTemplate[] } | WhatsAppTemplate[]>("/api/v1/whatsapp/templates")
      .then(r => setRows(Array.isArray(r) ? r : (r.data ?? [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [call]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const formik = useFormik<FormValues>({
    initialValues: EMPTY,
    validationSchema: schema,
    onSubmit: async (values) => {
      setSaving(true);
      try {
        if (editTarget?._id) {
          await call(`/api/v1/whatsapp/templates/${editTarget._id}`, { method: "PUT", body: values });
        } else {
          await call("/api/v1/whatsapp/templates", { method: "POST", body: values });
        }
        setDialogOpen(false); fetchAll();
      } finally { setSaving(false); }
    }
  });

  const openAdd = () => { setEditTarget(null); formik.resetForm({ values: EMPTY }); setDialogOpen(true); };
  const openEdit = (t: WhatsAppTemplate) => {
    setEditTarget(t);
    formik.resetForm({ values: { name: t.name, message: t.message ?? "", attachedType: t.attachedType } });
    setDialogOpen(true);
  };
  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    await call(`/api/v1/whatsapp/templates/${deleteTarget._id}`, { method: "DELETE" });
    setDeleteTarget(null); fetchAll();
  };

  const filtered = rows.filter(r =>
    `${r.name} ${r.message ?? ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: "name", headerName: t("whatsapp.templates.name"), width: 220,
      renderCell: (params: GridRenderCellParams<WhatsAppTemplate>) => (
        <Typography variant="body2" fontWeight={600}>{params.row.name}</Typography>
      )
    },
    {
      field: "message", headerName: t("whatsapp.templates.text"), flex: 1, minWidth: 300,
      renderCell: (params: GridRenderCellParams<WhatsAppTemplate>) => (
        <Typography variant="body2" color="text.secondary" sx={{
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          maxWidth: 400, display: "block"
        }}>
          {params.row.message}
        </Typography>
      )
    },
    {
      field: "attachedType", headerName: t("whatsapp.templates.attachment"), width: 180,
      renderCell: (params: GridRenderCellParams<WhatsAppTemplate>) => (
        <Chip size="small" variant="outlined" label={formLabel(params.row.attachedType)} sx={{ fontSize: "0.7rem" }} />
      )
    },
    {
      field: "actions", headerName: "", width: 90, sortable: false,
      renderCell: (params: GridRenderCellParams<WhatsAppTemplate>) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t("common.edit")}>
            <IconButton size="small" onClick={() => openEdit(params.row)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(params.row)}><DeleteIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader title={t("whatsapp.templates.title")} subtitle={t("whatsapp.templates.subtitle")}
        breadcrumbs={[{ label: t("sidebar.groupCommunications") }, { label: t("sidebar.templatesWa") }]}
        actionLabel={t("whatsapp.templates.newTemplate")} onAction={openAdd}
      />
      <Box sx={{ p: 3, flex: 1 }}>
        <Card>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
            <TextField size="small" placeholder={t("whatsapp.templates.searchPlaceholder")}
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment> }}
              sx={{ width: { xs: "100%", sm: 300 } }}
            />
          </Box>
          {!loading && filtered.length === 0
            ? <EmptyState title={t("whatsapp.templates.none")} description={t("whatsapp.templates.noneDesc")} actionLabel={t("whatsapp.templates.newTemplate")} onAction={openAdd} />
            : isMobile
              ? (
                <Box>
                  {filtered.map((r, idx) => (
                    <Box key={r._id ?? idx} sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700}>{r.name}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", mt: 0.25
                          }}>
                            {r.message}
                          </Typography>
                        </Box>
                        <DataGridRowActionsMenu
                          ariaLabel={t("common.actions")}
                          items={[
                            { key: "edit", label: t("whatsapp.templates.editAction"), description: t("whatsapp.templates.editActionDesc"), icon: <EditIcon fontSize="small" />, onClick: () => openEdit(r) },
                            { key: "delete", label: t("whatsapp.templates.deleteAction"), description: t("whatsapp.templates.deleteActionDesc"), icon: <DeleteIcon fontSize="small" />, destructive: true, dividerBefore: true, onClick: () => setDeleteTarget(r) }
                          ]}
                        />
                      </Box>
                      <Chip size="small" variant="outlined" label={formLabel(r.attachedType)} sx={{ fontSize: "0.7rem", mt: 1 }} />
                    </Box>
                  ))}
                </Box>
              )
              : <DataGrid rows={filtered} columns={columns} getRowId={r => r._id ?? Math.random().toString()}
                  loading={loading} autoHeight rowHeight={60} pageSizeOptions={[25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  disableRowSelectionOnClick sx={{ border: "none" }}
                />
          }
        </Card>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            {editTarget ? t("whatsapp.templates.editTemplate") : t("whatsapp.templates.newTemplate")}
            <DialogHelp steps={[{ title: t("help.formTitle"), description: t("help.forms.templates") }]} />
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={7}>
                <TextField fullWidth size="small" label={t("whatsapp.templates.nameLabel")}
                  {...formik.getFieldProps("name")}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("whatsapp.templates.attachmentLabel")}</InputLabel>
                  <Select label={t("whatsapp.templates.attachmentLabel")} {...formik.getFieldProps("attachedType")}>
                    {ATTACHED_TYPES.map(at => <MenuItem key={at} value={at}>{formLabel(at)}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label={t("whatsapp.templates.messageLabel")} multiline rows={4}
                  {...formik.getFieldProps("message")}
                  error={formik.touched.message && Boolean(formik.errors.message)}
                  helperText={formik.touched.message ? formik.errors.message : t("whatsapp.templates.messageHelper", { interpolation: { prefix: "[[", suffix: "]]" } })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? t("common.saving") : editTarget ? t("common.save") : t("whatsapp.templates.submitCreate")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        message={t("whatsapp.templates.deleteConfirm", { name: deleteTarget?.name })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
